/**
 * Provides global application configuration.
 * @type {{}}
 */

import TenantMgr from "../domains2/tenants/TenantMgr";
import AppError from "../domains2/libraries/AppError";
import businessModels from "../domains2/data/businessModels.json";
import getLuxonImpl from "../domains2/libraries/timezone/timeboxImpl";
import _ from "lodash";
import {TransientReleaseToggles} from "../domains2/transientReleaseToggles/TransientReleaseToggles";
import { App as NativeApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";

export const frequentlyUsedChannelEnum = Object.freeze({
	kiosk: "kiosk",
	desktopBrowser: "desktopBrowser",
	mobileBrowser: "mobileBrowser",
	mobileApp: "mobileApp"
});

export const channelEnum = Object.freeze({
	...frequentlyUsedChannelEnum,
	unknown: "unknown",
	admin: "admin",
	browser: "browser"
});

export const firebaseTopicsEnum = Object.freeze({
	general: "general",
	android: "android",
	ios: "ios",
	allMembers: "members",
	sunsetPlusBasicMembers: "sunsetplus-basic-members",
	sunsetPlusPremiumMembers: "sunsetplus-premium-members",
	sunsetPlusExclusiveMembers: "sunsetplus-exclusive-members",
	sunsetPlusBasicAnnualMembers: "sunsetplus-basic-annual-members",
	sunsetPlusPremiumAnnualMembers: "sunsetplus-premium-annual-members",
	sunsetPlusExclusiveAnnualMembers: "sunsetplus-exclusive-annual-members",
	sunsetPlusBusinessMembers: "sunsetplus-business-members",
	allNonMembers: "non-members",
	sunsetPoints: "sunsetpoints"
});

export const universalLinksEnum = Object.freeze({
	GENERAL: "general",
	SHOP_SELECTION: "shopSelection",
	MOBILE_PAY_FINISHED: "mobilePayFinished",
	SUBSCRIPTION_MOBILE_PAY_FINISHED: "subscriptionMobilePayFinished",
	MANAGE_AGREEMENT_INITIALIZED: "manageAgreementInitialized",
	DRIVE_IN_DETAILS_VIA_QRCODE: "driveInDetailsViaQrCode",
	GROUP_ORDER: "groupOrder"
});

const supportsWebP = () => {
	try {
		const canvas = document.createElement("canvas");
		if (canvas.getContext && canvas.getContext("2d")) {
			return canvas.toDataURL("image/webp").indexOf("data:image/webp") === 0;
		}
		return false;
	} catch (e) {
		return false;
	}
};

// default values
const config = {
	env: process.env, // eslint-disable-line no-undef
	businessModelsEnum: businessModels
};

config.supportsWebP = supportsWebP();

config.channelEnum = () => channelEnum;

config.frequentlyUsedChannelEnum = () => frequentlyUsedChannelEnum;

config.isBetaMode = () => !!BETA_MODE; // eslint-disable-line no-undef

config.universalLinksEnum = () => universalLinksEnum;

config.getBaseUrl = () => {
	// if specified different baseUrl use that one else default to serverFeathersBaseUrl
	return config.env.baseUrl ? config.env.baseUrl : config.env.serverFeathersBaseUrl;
};

config.getBaseApiFeathersUrl = () => {
	return config.env.serverFeathersBaseUrl + "/api";
};

config.getBaseApiNestUrl = () => {
	return config.env.serverNestBaseUrl + "/api/v2";
};

config.getPaymentUrl = () => {
	return config.getBaseUrl();
};

config.getPaymentSubscriptionUrl = () => {
	return config.getBaseUrl();
};

config.getContentUrl = () => {
	return `https://${config.isProdZone() ? "p" : "t"}-content.sprinting.io`;
};

config.getLoadedEnvZonePrefix = () => {
	return config.isProdZone() ? "prod" : "nonp";
};

config.cacheAppInfo = async () => {
	if (!Capacitor.isNativePlatform()) {
		return null;
	}
	const appInfo = await NativeApp.getInfo();
	localStorage.setItem("appInfo", JSON.stringify(appInfo));
	return appInfo;
};

config.getAppInfo = async () => {
	if (!Capacitor.isNativePlatform()) {
		return {version: "Browser"};
	}
	const cachedAppInfo = localStorage.getItem("appInfo");
	if (cachedAppInfo) {
		return JSON.parse(cachedAppInfo);
	}
	return config.cacheAppInfo();
};

config.mobile = {
	isMobileApp: () => Capacitor.isNativePlatform(),
	mobileEnvConfig: () => config.env.MOBILE,
	mobileApi: () => ({platformId: Capacitor.getPlatform()}),
	navigator: () => window.navigator,
	async getCurrentPositionAsync() {
		const platform = Capacitor.getPlatform();
		const options = {
			maximumAge: 300000,
			timeout: 5000
		};
		try {
			if (platform === "ios") {
				let permStatus = await Geolocation.checkPermissions();
				if (permStatus.location.includes("prompt")) {
					permStatus = await Geolocation.requestPermissions();
				}

				if (permStatus.location !== "granted") {
					throw new Error("User denied permissions!");
				}
			}

			const coordinates = await Geolocation.getCurrentPosition(options);
			return coordinates;
		} catch (e) {
			return null;
		}
	},
	isMobileBrowser: () => {
		const a = navigator.userAgent||navigator.vendor||window.opera;
		// eslint-disable-next-line no-useless-escape
		return /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4));
	}
};

config.loadedEnvironment = () => {
	if (config.mobile.isMobileApp()) {
		const serverUrl = config.getBaseApiFeathersUrl().replace(/(^\w+:|^)\/\//, ""); // remove protocol
		if (serverUrl.indexOf("localhost") > -1) {
			return "a-env";
		}

		const ENV = {
			"o": "o-env",
			"q": "q-env",
			"g": "g-env",
			"d": "d-env",
			"t": "t-env",
			"e": "e-env",
			"f": "f-env",
			"h": "h-env",
			"d1": "d1-env"
		};

		const firstPart = serverUrl.split("-");
		return ENV[firstPart[0]] ? ENV[firstPart[0]] : "p-env";
	} else {
		return LOADED_ENVIRONMENT; // eslint-disable-line no-undef
	}
};

console.log("Detected environment: " + config.loadedEnvironment()); // eslint-disable-line no-console

config.clientTraceId = () => CLIENT_TRACE_ID; // eslint-disable-line no-undef
config.isProductionEnvironment = () => IS_PRODF(); // eslint-disable-line no-undef
config.envPrefix = () => {
	return config.loadedEnvironment() ? config.loadedEnvironment().split("-")[0] : "";
};
config.isProdZone = () => {
	const prodEnvs = ["o", "p", "q"];
	const envPrefix = config.envPrefix();

	return prodEnvs.includes(envPrefix);
};

config.isNonProdZone = () => { return !config.isProdZone(); };

function getTenant() {
	if (typeof TENANT_ID === "undefined") {
		return 2;
	} else {
		return TENANT_ID; // eslint-disable-line no-undef
	}
}

config.envName = config.loadedEnvironment();

config.tenantId = getTenant();

config.tenantConfig = TenantMgr(config.tenantId).getTenantData();

config.tenantGroup = TenantMgr(config.tenantId).getTenantGroupData();

config.channelAlias = () => {
	const channel = CHANNEL_ALIAS ? CHANNEL_ALIAS : config.tenantConfig.defaultChannelAlias; // eslint-disable-line no-undef
	if (channel && Object.values(channelEnum).includes(channel)) {
		return channel;
	} else {
		throw AppError.createNewError(`InvalidChannel ${channel}`);
	}
};

config.getBusinessModelId = () => {
	const businessModelId = config.tenantConfig.businessModelId;

	if (businessModelId && Object.keys(businessModels).includes(businessModelId)) {
		return businessModelId;
	} else {
		throw AppError.createNewError(`InvalidBusinessModel ${businessModelId}`);
	}
};

config.isBM100 = () => {
	return config.getBusinessModelId() === config.businessModelsEnum.bm100.id;
};

config.isBM6 = () => {
	return config.getBusinessModelId() === config.businessModelsEnum.bm6.id;
};

config.paymentProcessingLogicHandle = () => PAYMENT_PROCESSING_LOGIC_HANDLE; // eslint-disable-line no-undef

config.initialUrl = () => INITIAL_URL; // eslint-disable-line no-undef

config.setShopNumberInInitialUrl = setShopNumberInInitialUrl; // eslint-disable-line no-undef

function getGlobalTemplateName() {
	const DEFAULT_GLOBAL_TEMPLATE = "defaultGlobalTemplate";

	if (typeof config.tenantConfig.globalTemplates !== "object") {
		return DEFAULT_GLOBAL_TEMPLATE;
	}

	const globalTemplates = config.tenantConfig.globalTemplates;

	if (typeof CHANNEL_ALIAS === "undefined") {
		// return default tenant global template
		return globalTemplates.defaultGlobalTemplate ? `gt${globalTemplates.defaultGlobalTemplate}` : DEFAULT_GLOBAL_TEMPLATE;
	}

	const channel = Array.isArray(globalTemplates.channels)
		? globalTemplates.channels.find(c => c.channelAlias === CHANNEL_ALIAS) : undefined; // eslint-disable-line no-undef

	if (channel) {
		return channel.globalTemplateId ? `gt${channel.globalTemplateId}` : DEFAULT_GLOBAL_TEMPLATE;
	} else {
		return globalTemplates.defaultGlobalTemplate ? `gt${globalTemplates.defaultGlobalTemplate}` : DEFAULT_GLOBAL_TEMPLATE;
	}
}

function getStyleVariant() {
	if (config.channelAlias() === channelEnum.kiosk) {
		return localStorage.getItem("styleVariant");
	}

	return undefined;
}

config.preselectedShopNumber = () => SHOP_NUMBER; // eslint-disable-line no-undef

config.globalTemplateName = getGlobalTemplateName();
config.styleVariant = getStyleVariant();

config.fbPixel = window.fbq ? window.fbq : () => {};

config.googleTagManagerDataLayer = window.dataLayer ? window.dataLayer : []; // google tag manager

config.browserInfo = (function () {
	const ua = navigator.userAgent;
	let tem, M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];

	if (/trident/i.test(M[1])) {
		tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
		return {name: "IE", version: (tem[1] || "")};
	}
	if (M[1] === "Chrome") {
		tem = ua.match(/\b(OPR|Edge)\/(\d+)/);
		if (tem !== null) return {name: tem[1].replace("OPR", "Opera"), version: tem[2]};
	}
	M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, "-?"];
	if ((tem = ua.match(/version\/(\d+)/i)) !== null)
		M.splice(1, 1, tem[1]);
	return {name: M[0], version: M[1]};
})();

config.platform = (function () {
	const userAgent = navigator.userAgent;
	// search for first occurrence of open and closed brackets, and extract text between
	const openingBracket = userAgent.indexOf("(");
	const closingBracket = userAgent.indexOf(")");
	const platformInformation = userAgent.substr(openingBracket+1,closingBracket-openingBracket-1);

	// always choose second entry
	const platformVersion = platformInformation.split(";")[1];
	const platformId = window.navigator.platform;
	return {platformId, platformVersion};
})();

config.timeBoxImpl = getLuxonImpl();
config.transientReleaseToggles = TransientReleaseToggles(config.loadedEnvironment(), config.tenantId);
config.apmRumConfig = {
	"bm2": {
		"protocol": "https",
		"hostname": "rum.dsgapps.dk",
		"port": 443,
		"serviceName": "BifrostShop",
		"serviceVersion": "1.0",
		"enabled": true,
		"transactionSampleRate": 0.1,
		"flushInterval": 5000,
		"centralConfig": true
	},
	"bm3": {
		"protocol": "https",
		"hostname": "rum.dsgapps.dk",
		"port": 443,
		"serviceName": "BifrostShop",
		"serviceVersion": "1.0",
		"enabled": true,
		"transactionSampleRate": 0.1,
		"flushInterval": 5000
	},
	"bm6": {
		"protocol": "https",
		"hostname": "rum.dsgapps.dk",
		"port": 443,
		"serviceName": "BifrostShop",
		"serviceVersion": "1.0",
		"enabled": false,
		"transactionSampleRate": 0.1,
		"flushInterval": 5000
	},
	"bm100": {
		"protocol": "https",
		"hostname": "apmrum.sprinting.io",
		"port": 443,
		"serviceName": "BifrostShop",
		"serviceVersion": "1.0",
		"enabled": true,
		"transactionSampleRate": config.isProdZone() ? 0.05 : 1,
		"flushInterval": 5000,
		"centralConfig": true
	}
};

config.gboAppUrl = () => {
	return config.env.gboAppUrl;
};

config.loyaltyAppUrl = () => {
	return config.env.loyaltyAppUrl;
};

function getGlobalTemplateReplaceCss() {
	const globalTemplates = _.get(config, "tenantConfig.globalTemplates", {});

	const channel = Array.isArray(globalTemplates.channels)
		? globalTemplates.channels.find(c => c.channelAlias === CHANNEL_ALIAS) : undefined; // eslint-disable-line no-undef

	if (channel) {
		return channel.replaceCss;
	}

	return undefined;
}
config.globalTemplateReplaceCss = getGlobalTemplateReplaceCss();

export default Object.freeze(config);
